import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
import clsx from "clsx";
import dayjs from "dayjs";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import Select from "components/Select";
import StatusModal from "components/StatusModal";
import { CSVSvgIcon } from "components/SVGIcons";
import { BROKER_FR_STATUS_LABELS, CARRIER_FR_STATUS_LABELS, ORDER_LIST } from "constants/base";
import { CARRIER_FR_CSV_HEADER } from "constants/haulpay-carrier";
import { routes } from "constants/routes";
import CarrierFundingRequestItem from "features/main/HaulPay/Shared/CarrierFundingRequestItem";
import ListPageMobileHeader from "features/main/HaulPay/Shared/ListPageMobileHeader";
import {
  MUTATION_UPDATE_BROKER_FUNDING_REQUEST_STATUS,
  MUTATION_UPDATE_BROKER_FUNDING_REQUEST_TYPE
} from "features/main/Loads/Post/FundingRequest/query";
import { parseError } from "helpers/convertFormData";
import { formatNumber } from "helpers/formatNumber";
import { showErrorMessage } from "helpers/toastMessage";
import { useUserFeature } from "hooks/useUserFeature";
import MainLayout from "layouts/Main";
import { IOption } from "models/Form";
import { IFundingRequestItem } from "models/HaulPay";
import { selectUserDetails } from "store/user/selector";
import usePaginatedQuery from "hooks/usePaginatedQuery";
import { Filters } from "../../Broker/ManagePay";
import { QUERY_FUNDING_REQUEST_BY_ME } from "../../Broker/ManagePay/query";
import { QUERY_CARRIER_FUNDING_REQUEST_BY_ME } from "../../Carrier/ManagePay/query";
import BrokerFundingRequestItem from "../../Shared/BrokerFundingRequestItem";
import { NEW_FUNDING_REQUEST_TYPE, NEW_STATUS_LIST } from "../Broker/ManagePayList";
import SearchInput from "../FRListByStatusDesktop/components/SearchInput";
import styles from "./style.module.scss";
import LoadingSpinner, { RefLoadingSpinner } from "components/LoadingSpinner";
import usePaginatedInfiniteScroll from "hooks/usePaginatedInfiniteScroll";
import FRTypeFilterDropdown from "../FRTypeFilterDropdown";
import { FR_CONTRACT_TYPE } from "../../../../../constants/haulpay-broker";
import { selectAccount } from "../../../../../store/auth/selector";

type FormValues = {
  state: IOption;
  type: IOption;
};

interface ManagePayHistoryState {
  debtor?: string;
  debtorName?: string;
  searchValue?: string;
  frTypeOption?: IOption;
}

export default function FRListByStatusMobile() {
  const navigate = useNavigate();
  const { status } = useParams<{ status: string }>();
  const location = useLocation();
  const state = location.state as ManagePayHistoryState | undefined;
  const userDetails = useSelector(selectUserDetails);
  const { isBroker, isCarrier, isNonFactorRefactorEnabled } = useUserFeature();
  const { userPermission } = useSelector(selectAccount);
  const pageTitle = !isBroker
    ? CARRIER_FR_STATUS_LABELS[status ?? "other"]
    : BROKER_FR_STATUS_LABELS[status ?? "other"];
  const orderList = ORDER_LIST.map((item) => ({
    id: item.id,
    name: item.name,
    value: item.managePay
  }));
  const [filters, setFilters] = useState<Filters>({
    debtor: state?.debtor ?? "",
    debtorName: state?.debtorName ?? "",
    loadOrInvoiceOrDebtorOrStatus: state?.searchValue ?? null,
    orderBy: "-created",
    status: "incomplete",
    contractType: state?.frTypeOption?.value ?? null
  });

  const { data, loading, loadNextPage, pageInfo, currentPage } = usePaginatedQuery<
    Record<string, any>,
    IFundingRequestItem
  >({
    query: isBroker ? QUERY_FUNDING_REQUEST_BY_ME : QUERY_CARRIER_FUNDING_REQUEST_BY_ME,
    variables: {
      debtor: filters.debtor,
      ...(status !== "all" && { status: status }),
      orderBy: filters.orderBy,
      loadOrInvoiceOrDebtorOrStatus: filters.loadOrInvoiceOrDebtorOrStatus,
      contractType: filters.contractType
    },
    cursorField: "fundingRequestsByMe",
    pageSize: 20,
    options: {
      notifyOnNetworkStatusChange: true
    },
    loadMore: true
  });

  const { containerRef, loadMoreRef } = usePaginatedInfiniteScroll({
    loadMore: loadNextPage,
    hasMore: pageInfo.hasNextPage,
    loading: loading,
    threshold: 200
  });

  const [updateFRStatus] = useMutation(MUTATION_UPDATE_BROKER_FUNDING_REQUEST_STATUS, {
    onError(err) {
      err.graphQLErrors.forEach(({ message }) => showErrorMessage(parseError(message)));
    }
  });
  const [updateFRType] = useMutation(MUTATION_UPDATE_BROKER_FUNDING_REQUEST_TYPE, {
    onError(err) {
      err.graphQLErrors.forEach(({ message }) => showErrorMessage(parseError(message)));
    }
  });

  const {
    control,
    getValues,
    setValue,
    formState: { isSubmitting },
    handleSubmit
  } = useForm<FormValues>({
    defaultValues: {
      state: NEW_STATUS_LIST[0],
      type: NEW_FUNDING_REQUEST_TYPE[0]
    }
  });
  const [selectedFRs, setSelectedFRs] = useState<string[]>([]);
  const [typeToUpdate, setTypeToUpdate] = useState<"status" | "type">("status");
  const [isSuccessModalOpen, toggleSuccessModal] = useToggle();

  const toggleSelection = useCallback((id: string) => {
    setSelectedFRs((prevSelected) => {
      if (prevSelected.includes(id)) {
        return prevSelected.filter((frId) => frId !== id);
      } else {
        return [...prevSelected, id];
      }
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedFRs.length === data.length) {
      setSelectedFRs([]);
    } else {
      setSelectedFRs(data.map((fr) => fr.id));
    }
  }, [selectedFRs, data]);

  const goToMoreDetails = useCallback(
    (id: string) => {
      navigate(
        `${isBroker ? routes.HAULPAY_BROKER_FUNDING_REQUEST : routes.HAULPAY_CARRIER_MANGE_PAY_PAYMENT_DETAILS}/${id}`
      );
    },
    [isBroker, navigate]
  );

  // Define loading states
  const isInitialLoading = loading && currentPage === 1;
  const isLoadingMore = loading && currentPage > 1;

  const onUpdateFR = useCallback(
    async ({ state, type }: FormValues) => {
      try {
        if (state.value === "none") {
          // Update type
          await Promise.all(
            selectedFRs.map((id) =>
              updateFRType({
                variables: {
                  id,
                  contractType: type.value,
                  selfFinanceRequested: type.label !== "Factored"
                }
              })
            )
          );
          setSelectedFRs([]);
          toggleSuccessModal();
        } else if (type.value === "none") {
          // Update status
          await Promise.all(
            selectedFRs.map((id) =>
              updateFRStatus({
                variables: {
                  id,
                  status: state.value
                }
              })
            )
          );
          setSelectedFRs([]);
          toggleSuccessModal();
        } else {
          showErrorMessage("Please select the correct item.");
        }
      } catch (error) {
        // Error handling is already managed in mutations
      }
    },
    [selectedFRs, updateFRStatus, updateFRType, toggleSuccessModal]
  );

  useEffect(() => {
    if (typeToUpdate === "status") {
      setValue("type", NEW_FUNDING_REQUEST_TYPE[0]);
    } else {
      setValue("state", NEW_STATUS_LIST[0]);
    }
  }, [typeToUpdate, setValue]);

  const totalRevenue = useMemo(() => {
    return data.reduce((prev, fr) => prev + Number(fr.amount), 0);
  }, [data]);

  const csvData = useMemo(() => {
    return data.map((fr) => ({
      invoiceNumber: fr?.invoiceNumber,
      userLoadNumber: fr?.userLoadNumber,
      created: dayjs(fr?.created).format("YYYY-MM-DD"),
      status: fr?.status,
      debtor: fr?.debtor?.companyName,
      amount: formatNumber(fr?.amount, 2),
      fundedAt: dayjs(fr?.fundedAt).format("YYYY-MM-DD"),
      fuelAdvancePaymentNumber: "",
      fuelAdvanceFee: formatNumber(fr?.fuelAdvanceFee / 100, 2),
      fuelAdvanceAmount: formatNumber(Number(fr?.fuelAdvanceAmount) / 100, 2),
      modified: dayjs(fr?.modified).format("YYYY-MM-DD"),
      discountRate: fr?.discountRate + "%",
      discountAmount: formatNumber((fr?.amount * Number(fr?.discountRate)) / 100, 2),
      chargeback: "",
      giveback: "",
      paymentNumber: "",
      totalPay: formatNumber(fr?.amount - (fr?.amount * Number(fr?.discountRate)) / 100, 2),
      clientInvoiceNumber: fr?.clientInvoiceNumber
    }));
  }, [data]);

  const csvFileName = `Funding_Request_report_${userDetails?.dba}_${dayjs().format("DD_MM_YYYY")}.csv`;

  const updateFilter = useCallback(
    (key: keyof Filters, value: Filters[keyof Filters]) => {
      setFilters((prevFilters) => {
        if (prevFilters[key] === value) {
          return prevFilters;
        }
        return {
          ...prevFilters,
          [key]: value
        };
      });
    },
    [setFilters]
  );

  const handleSearch = useCallback(
    (value: string | null, key: keyof Filters) => {
      updateFilter(key, value);
      updateFilter("contractType", null);
    },
    [updateFilter]
  );

  const handleLinkClick = useCallback(() => {
    updateFilter("debtor", "");
    updateFilter("debtorName", "");
  }, [filters]);

  const handleFrTypeFilterChange = (selectedFrType: IOption) => {
    updateFilter("contractType", selectedFrType.value);
  };
  const handleFrTypeFilterReset = () => {
    updateFilter("contractType", null);
  };

  const frTypeOptions = FR_CONTRACT_TYPE.filter(
    (request) => userPermission.available_contract_types[request.value]
  );

  return (
    <MainLayout
      customHeader={
        <ListPageMobileHeader
          title={pageTitle}
          orderList={orderList}
          selectedOrder={filters.orderBy || "-created"}
          setSelectedOrder={(value) => handleSearch(value, "orderBy")}
        />
      }
      className={clsx("px-5", data.length > 0 ? "pb-40" : "pb-24")}
      hideNavigation={selectedFRs.length > 0}>
      {isInitialLoading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner isFetching={isInitialLoading} />
        </div>
      ) : (
        <div ref={containerRef}>
          <SearchInput
            initialValue={filters.loadOrInvoiceOrDebtorOrStatus || ""}
            onSearch={(value) => handleSearch(value ?? "", "loadOrInvoiceOrDebtorOrStatus")}
            isMobile={true}
            iconClassName={styles.searchIcon}
          />
          {isBroker && isNonFactorRefactorEnabled && frTypeOptions.length > 0 && (
            <FRTypeFilterDropdown
              style={{ flex: "0", marginTop: "18px" }}
              frTypeOptions={frTypeOptions}
              handleOnChange={handleFrTypeFilterChange}
              handleResetClick={handleFrTypeFilterReset}
              value={filters.contractType}
            />
          )}
          {!!filters.debtorName && (
            <div className={styles.content}>
              <p className="mt-4 text-sm font-bold">
                Displaying funding requests for {filters.debtorName}
              </p>
              <button
                onClick={handleLinkClick}
                className={clsx(styles.clearFilter, "text-sm text-blue-500")}>
                Clear Filter
              </button>
            </div>
          )}
          {isBroker && !!data.length && (
            <button
              className={clsx(styles.selectAllButton, "text-sm text-blue-500 mt-4")}
              onClick={handleSelectAll}>
              {selectedFRs.length === data.length ? "Deselect All" : "Select All"}
            </button>
          )}

          {isCarrier && data.length > 0 && (
            <div className={clsx(styles.totalRevenue, "flex justify-between items-center mt-4")}>
              <span
                className={clsx(
                  styles.description,
                  "text-sm"
                )}>{`Total Revenue (From - To date): ${formatNumber(totalRevenue)}`}</span>
              <div className="flex items-center space-x-2">
                <CSVLink
                  data={csvData}
                  headers={CARRIER_FR_CSV_HEADER}
                  filename={csvFileName}
                  target="_blank">
                  <CSVSvgIcon />
                </CSVLink>
              </div>
            </div>
          )}

          {data.length === 0 && !isInitialLoading && (
            <div className="flex items-center justify-center mt-10">
              <p className="text-gray-500">No funding requests found.</p>
            </div>
          )}

          <div className="flex flex-col gap-4 mt-4">
            {data.map((item) =>
              isBroker ? (
                <BrokerFundingRequestItem
                  key={item.id}
                  data={item}
                  isSelected={selectedFRs.includes(item.id)}
                  handleClick={toggleSelection}
                  goToMoreDetails={() => goToMoreDetails(item.id)}
                />
              ) : (
                <CarrierFundingRequestItem
                  key={item.id}
                  data={item}
                  goToMoreDetails={() => goToMoreDetails(item.id)}
                />
              )
            )}
          </div>
          {isBroker && selectedFRs.length > 0 && (
            <form
              className={clsx(
                styles.footerContainer,
                "fixed bottom-0 left-0 right-0 bg-white p-4 shadow-lg"
              )}
              onSubmit={handleSubmit(onUpdateFR)}>
              <div className="p-4 bg-gray-100 rounded-lg">
                <Controller
                  name="state"
                  control={control}
                  render={({ field: { value = NEW_STATUS_LIST[0], onChange } }) => (
                    <Select
                      value={value}
                      onChange={(value: IOption) => {
                        setTypeToUpdate("status");
                        onChange(value);
                      }}
                      options={NEW_STATUS_LIST}
                      label="Set Status"
                      direction="up"
                    />
                  )}
                />
                <Controller
                  name="type"
                  control={control}
                  render={({ field: { value = NEW_FUNDING_REQUEST_TYPE[0], onChange } }) => (
                    <Select
                      options={NEW_FUNDING_REQUEST_TYPE}
                      value={value}
                      onChange={(value: IOption) => {
                        setTypeToUpdate("type");
                        onChange(value);
                      }}
                      label="Set Type"
                      direction="up"
                    />
                  )}
                />
              </div>
              <Button type="submit" rounded className="w-full mt-2 h-14" loading={isSubmitting}>
                SUBMIT
              </Button>
            </form>
          )}
          {isBroker && (
            <StatusModal
              title="Status Updated"
              content={`Successfully set ${typeToUpdate} to ${
                typeToUpdate === "status" ? getValues("state").label : getValues("type").label
              }.`}
              isModalOpen={isSuccessModalOpen}
              toggleModal={toggleSuccessModal}
              backdropClick={toggleSuccessModal}
            />
          )}
          <RefLoadingSpinner observerRef={loadMoreRef} isLoadingMore={isLoadingMore} />
        </div>
      )}
    </MainLayout>
  );
}
